import IconText from '@picsart/icons/IconText';
import IconArtisticBrushes from '@picsart/icons/IconArtisticBrushes';
import IconAITools from '@picsart/icons/IconAITools';
import IconCreate from '@picsart/icons/IconCreate';
import IconSketch from '@picsart/icons/IconSketch';
import IconBlankWrite from '@picsart/icons/IconBlankWrite';
import IconDetails from '@picsart/icons/IconDetails';
import IconJob from '@picsart/icons/IconJob';
import IconPromote from '@picsart/icons/IconPromote';
import IconExportVideo from '@picsart/icons/IconExportVideo';
import IconColorExposure from '@picsart/icons/IconColorExposure';
import IconArrowRightOutline from '@picsart/icons/IconArrowRightOutline';
import IconPDF from '@picsart/icons/IconPDF';
import IconBlog from '@picsart/icons/IconBlog';
import IconBurnOutline from '@picsart/icons/IconBurnOutline';

import { PicsartIcon } from '../../types/other';
import { IBottomLink } from '../tools/types';
import { getNavLink } from '../../utils/nav';
import {
  IToolData,
  PHOTO_CAPTION,
  VIDEO_TOOLS,
  COLOR_TOOLS,
  CONVERT_TOOLS,
  PDF_TOOLS,
} from '../tools';
import {
  IMAGE_TOOLS,
  DESIGN_TOOLS,
  FONT_GENERATOR_TOOL,
  EMOJIS_TOOL,
  SYMBOLS_TOOL,
  WORD_COUNT_TOOL,
} from '../tools';
import { ICON_COLOR, ICON_BACKGROUND, ICON_FILL } from '../colors';

import PicsartLogo from './PicsartLogo';

export interface INavLink {
  id: string;
  icon: PicsartIcon;
  navLink: string;
  label: string;
  subLinks: IToolData[];
  iconColor?: string;
  fillColor?: string;
  bottomLinks: IBottomLink[];
}

export const NAVBAR_LINKS: INavLink[] = [
  {
    id: 'Text',
    label: 'Text',
    subLinks: [FONT_GENERATOR_TOOL, EMOJIS_TOOL, SYMBOLS_TOOL, WORD_COUNT_TOOL, PHOTO_CAPTION],
    navLink: getNavLink('/text'),
    icon: IconText,
    iconColor: ICON_BACKGROUND[ICON_COLOR.blue],
    fillColor: ICON_FILL[ICON_COLOR.blue],
    bottomLinks: [
      {
        text: 'Go to the text page',
        href: getNavLink('/text'),
      },
    ],
  },
  {
    id: 'AiWriter',
    label: 'AI Writer',
    subLinks: [
      {
        id: 'gen_text',
        name: 'Generate text',
        description: '',
        href: '',
        icon: {
          fill: ICON_FILL[ICON_COLOR.blue],
          src: IconSketch,
          width: 20,
          height: 20,
          background: ICON_BACKGROUND[ICON_COLOR.blue],
        },
        subCategories: [
          {
            label: 'Article',
            id: 'gen_article',
            href: getNavLink('/text/ai-article-writer'),
          },
          {
            label: 'Email',
            id: 'gen_email',
            href: getNavLink('/text/ai-writer/email-writer'),
          },
          {
            label: 'Essay',
            id: 'gen_essay',
            href: getNavLink('/text/ai-essay-writer'),
          },
          {
            label: 'Ideas',
            id: 'gen_ideas',
            href: getNavLink('/text/idea-generator'),
          },
          {
            label: 'Names',
            id: 'gen_names',
            href: getNavLink('/text/business-name-generator'),
          },
          {
            label: 'Outline',
            id: 'gen_outline',
            href: getNavLink('/text/outline-generator'),
          },
          {
            label: 'Paragraph',
            id: 'gen_paragraph',
            href: getNavLink('/text/ai-writer/paragraph-generator'),
          },
          {
            label: 'Prompt',
            id: 'gen_prompt',
            href: getNavLink('/text/prompt-generator'),
          },
          {
            label: 'Script',
            id: 'gen_video_script',
            href: getNavLink('/text/ai-writer/video-script-generator'),
          },
          {
            label: 'Story',
            id: 'gen_story',
            href: getNavLink('/text/ai-story-generator'),
          },
          {
            label: 'Titles',
            id: 'gen_titles',
            href: getNavLink('/text/ai-title-generator'),
          },
          {
            label: 'Translations',
            id: 'gen_translations',
            href: getNavLink('/text/ai-writer/translator'),
          },
        ],
      },
      {
        id: 'edit_text',
        name: 'Edit text',
        description: '',
        href: '',
        icon: {
          fill: ICON_FILL[ICON_COLOR.green],
          src: IconBlankWrite,
          width: 20,
          height: 20,
          background: ICON_BACKGROUND[ICON_COLOR.green],
        },
        subCategories: [
          {
            label: 'Expand',
            id: 'expand_text',
            href: getNavLink('/text/ai-writer/expand-text/'),
          },
          {
            label: 'Grammar checker',
            id: 'edit_grammar',
            href: getNavLink('/text/ai-grammar-checker'),
          },
          {
            label: 'Paraphraser',
            id: 'edit_repharser',
            href: getNavLink('/text/paraphrase'),
          },
          {
            label: 'Shorten',
            id: 'shorten_text',
            href: getNavLink('/text/ai-writer/shorten-text'),
          },
          {
            label: 'Summarizer',
            id: 'edit_summarizer',
            href: getNavLink('/text/summarizer'),
          },
        ],
      },
      {
        id: 'content',
        name: 'Content',
        description: '',
        href: '',
        icon: {
          fill: ICON_FILL[ICON_COLOR.green],
          src: IconDetails,
          width: 20,
          height: 20,
          background: ICON_BACKGROUND[ICON_COLOR.green],
        },
        subCategories: [
          {
            label: 'Article',
            id: 'con_article',
            href: getNavLink('/text/ai-article-writer'),
          },
          {
            label: 'Blog titles',
            id: 'con_blog_titles',
            href: getNavLink('/text/ai-writer/title-generator/blog'),
          },
          {
            label: 'Captions',
            id: 'con_captions',
            href: getNavLink('/text/instagram-caption-generator'),
          },
          {
            label: 'Hashtags',
            id: 'con_hashtags',
            href: getNavLink('/text/instagram-hashtag-generator'),
          },
          {
            label: 'Ideas',
            id: 'con_ideas',
            href: getNavLink('/text/idea-generator'),
          },
          {
            label: 'Meta description',
            id: 'con_meta',
            href: getNavLink('/text/ai-writer/meta-description-generator'),
          },
          {
            label: 'Outline',
            id: 'con_outline',
            href: getNavLink('/text/ai-writer/landing-page-outline'),
          },
          {
            label: 'Paragraph',
            id: 'con_paragraph',
            href: getNavLink('/text/ai-writer/paragraph-generator'),
          },
          {
            label: 'Script',
            id: 'con_video_script',
            href: getNavLink('/text/ai-writer/video-script-generator'),
          },
          {
            label: 'Social media bio',
            id: 'con_bio',
            href: getNavLink('/text/ai-writer/bio-generator'),
          },
          {
            label: 'Story ideas',
            id: 'con_story',
            href: getNavLink('/text/ai-writer/idea-generator/story'),
          },
          {
            label: 'Titles',
            id: 'con_titles',
            href: getNavLink('/text/ai-title-generator'),
          },
          {
            label: 'Video titles',
            id: 'con_video',
            href: getNavLink('/text/ai-writer/title-generator/video'),
          },
        ],
      },
      {
        id: 'business_text',
        name: 'Business',
        description: '',
        href: '',
        icon: {
          fill: ICON_FILL[ICON_COLOR.green],
          src: IconJob,
          width: 20,
          height: 20,
          background: ICON_BACKGROUND[ICON_COLOR.green],
        },
        subCategories: [
          {
            label: 'Business names',
            id: 'business_name',
            href: getNavLink('/text/business-name-generator'),
          },
          {
            label: 'Email writer',
            id: 'business_email',
            href: getNavLink('/text/email-writer/'),
          },
          {
            label: 'Ideas',
            id: 'business_ideas',
            href: getNavLink('/text/idea-generator'),
          },
          {
            label: 'Side hustle ideas',
            id: 'business_hustle',
            href: getNavLink('/text/ai-writer/idea-generator/side-hustle'),
          },
          {
            label: 'Slogan',
            id: 'business_slogan',
            href: getNavLink('/text/slogan-maker'),
          },
          {
            label: 'Startup',
            id: 'business_startup',
            href: getNavLink('/text/ai-writer/idea-generator/startup'),
          },
        ],
      },
      {
        id: 'marketing',
        name: 'Marketing',
        description: '',
        href: '',
        icon: {
          fill: ICON_FILL[ICON_COLOR.green],
          src: IconPromote,
          width: 20,
          height: 20,
          background: ICON_BACKGROUND[ICON_COLOR.green],
        },
        subCategories: [
          {
            label: 'Ad copy',
            id: 'marketing_grammar',
            href: getNavLink('/text/ads-copy-generator'),
          },
          {
            label: 'Article',
            id: 'marketing_article',
            href: getNavLink('/text/ai-article-writer'),
          },
          {
            label: 'Blog titles',
            id: 'marketing_blog_titles',
            href: getNavLink('/text/ai-writer/title-generator/blog'),
          },
          {
            label: 'Email',
            id: 'marketing_email',
            href: getNavLink('/text/ai-writer/email-writer'),
          },
          {
            label: 'Landing page titles',
            id: 'marketing_lp',
            href: getNavLink('/text/ai-writer/title-generator/landing-page'),
          },
          {
            label: 'Meta description',
            id: 'marketing_meta',
            href: getNavLink('/text/meta-description-generator'),
          },
          {
            label: 'Paragraph',
            id: 'marketing_paragraph',
            href: getNavLink('/text/ai-writer/paragraph-generator'),
          },
          {
            label: 'Product description',
            id: 'marketing_product',
            href: getNavLink('/text/product-description-generator'),
          },
          {
            label: 'Slogan',
            id: 'marketing_slogan',
            href: getNavLink('/text/slogan-maker'),
          },
        ],
      },
    ],
    navLink: getNavLink('/text/ai-writer'),
    icon: IconArtisticBrushes,
    iconColor: ICON_BACKGROUND[ICON_COLOR.lightGreen],
    fillColor: ICON_FILL[ICON_COLOR.lightGreen],
    bottomLinks: [
      {
        text: 'Open AI writer',
        href: getNavLink('/text/ai-writer/create'),
      },
      {
        text: 'Go to the AI writer page',
        href: getNavLink('/text/ai-writer'),
      },
    ],
  },
  {
    id: 'Image',
    label: 'Image',
    subLinks: IMAGE_TOOLS,
    navLink: getNavLink('/image'),
    icon: IconAITools,
    iconColor: ICON_BACKGROUND[ICON_COLOR.magenta],
    fillColor: ICON_FILL[ICON_COLOR.magenta],
    bottomLinks: [
      {
        text: 'Go to the Image page',
        href: getNavLink('/image'),
      },
    ],
  },
  {
    id: 'Design',
    label: 'Design',
    subLinks: DESIGN_TOOLS,
    navLink: getNavLink('/design'),
    icon: IconCreate,
    iconColor: ICON_BACKGROUND[ICON_COLOR.purple],
    fillColor: ICON_FILL[ICON_COLOR.purple],
    bottomLinks: [
      {
        text: 'Go to the design page',
        href: getNavLink('/design'),
      },
    ],
  },
  {
    id: 'Other',
    label: 'Other',
    subLinks: [
      {
        id: 'video',
        name: 'Video',
        description: '',
        href: '/video/',
        icon: {
          fill: ICON_FILL[ICON_COLOR.yellow],
          src: IconExportVideo,
          width: 20,
          height: 20,
          background: ICON_BACKGROUND[ICON_COLOR.yellow],
        },
        subCategories: VIDEO_TOOLS.map((tool) => ({
          label: tool.name,
          id: tool.id,
          href: tool.href,
        })),
        bottomLinks: [
          {
            text: 'Go to the video page',
            href: '/video/',
          },
        ],
      },
      {
        id: 'color',
        name: 'Color',
        description: '',
        href: '/color/',
        icon: {
          fill: ICON_FILL[ICON_COLOR.purple],
          src: IconColorExposure,
          width: 20,
          height: 20,
          background: ICON_BACKGROUND[ICON_COLOR.purple],
        },
        subCategories: COLOR_TOOLS.map((tool) => ({
          label: tool.name,
          id: tool.id,
          href: tool.href,
        })),
        bottomLinks: [
          {
            text: 'Go to the color page',
            href: '/color/',
          },
        ],
      },
      {
        id: 'convert',
        name: 'Convert',
        description: '',
        href: '/convert/',
        icon: {
          fill: ICON_FILL[ICON_COLOR.green],
          src: IconArrowRightOutline,
          width: 20,
          height: 20,
          background: ICON_BACKGROUND[ICON_COLOR.green],
        },
        subCategories: CONVERT_TOOLS.map((tool) => ({
          label: tool.name,
          id: tool.id,
          href: tool.href,
        })),
        bottomLinks: [
          {
            text: 'Go to the convert page',
            href: '/convert/',
          },
        ],
      },
      {
        id: 'pdf',
        name: 'PDF',
        description: '',
        href: '/pdf/',
        icon: {
          fill: ICON_FILL[ICON_COLOR.red],
          src: IconPDF,
          width: 20,
          height: 20,
          background: ICON_BACKGROUND[ICON_COLOR.red],
        },
        subCategories: PDF_TOOLS.map((tool) => ({
          label: tool.name,
          id: tool.id,
          href: tool.href,
        })),
        bottomLinks: [
          {
            text: 'Go to the PDF page',
            href: '/pdf/',
          },
        ],
      },
      {
        id: 'picsart',
        name: 'Picsart',
        description: '',
        href: '',
        icon: {
          fill: 'none',
          src: PicsartLogo,
          width: 20,
          height: 20,
          background: 'none',
        },
        subCategories: [
          {
            id: 'pe',
            label: 'Photo editor',
            href: 'https://picsart.com/photo-editor',
            openInNewTab: true,
          },
          {
            id: 'aie',
            label: 'AI image enhancer',
            href: 'https://picsart.com/ai-image-enhancer',
            openInNewTab: true,
          },
          {
            id: 'aig',
            label: 'AI image generator',
            href: 'https://picsart.com/ai-image-generator',
            openInNewTab: true,
          },
          {
            id: 'bc',
            label: 'Background changer',
            href: 'https://picsart.com/background-changer ',
            openInNewTab: true,
          },
          {
            id: 'cm',
            label: 'Collage maker',
            href: 'https://picsart.com/collage-maker',
            openInNewTab: true,
          },
          {
            id: 'brem',
            label: 'Background remover',
            href: 'https://picsart.com/background-remover',
            openInNewTab: true,
          },
        ],
        bottomLinks: [
          {
            text: 'Go to Picsart all-in-one editor',
            href: 'https://picsart.com/create',
          },
        ],
      },
      {
        id: 'blog',
        name: 'Blog',
        description: '',
        href: '/blog/',
        icon: {
          fill: '#808080',
          src: IconBlog,
          width: 20,
          height: 20,
          background: '#F2F2F2',
        },
        iconRound: true,
      },
    ],
    navLink: '',
    icon: IconBurnOutline,
    bottomLinks: [],
  },
];
